// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Alert, Grid } from 'antd';
import translateX, { useLanguageUpdate } from 'utils/translateX';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';

// Section Components
import AiMetaSection from './AiMetaSection';
import SearchResultsSection from './SearchResultsSection';
import ProductDataSection from './ProductDataSection';
import ClassificationSection from './ClassificationSection';
import TechnicalSection from './TechnicalSection';
import FiscalDataSection from './FiscalDataSection';
import ManufacturerSection from './ManufacturerSection';
import PricingSection from './PricingSection';
import DimensionsSection from './DimensionsSection';

// Segment-Specific Sections
import InsuranceDataSection from './InsuranceDataSection';
import LicenseDataSection from './LicenseDataSection';
import SubscriptionDataSection from './SubscriptionDataSection';
import InfoproductDataSection from './InfoproductDataSection';
import RealestateDataSection from './RealestateDataSection';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * AiInfoTab - Displays AI cataloging information for the product
 */
const AiInfoTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, loading } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  useLanguageUpdate();
  const screens = Grid.useBreakpoint();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = !screens.md;
  const hasAiMeta = data?.aiCatalogMeta?.catalogedByAI === true;
  const segment = data?.productSegment;
  const isPhysicalOrKit = !segment || segment === 'physical' || segment === 'kit';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasAiMeta) {
    return (
      <div className="container" style={{ padding: isMobile ? 16 : 24 }}>
        <Alert
          type="info"
          showIcon
          title={translateX('product_not_cataloged_by_ai')}
          description={translateX('product_not_cataloged_by_ai_description')}
        />
      </div>
    );
  }

  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="aiInfo" isFetching={loading} data={data} />

      {/* AI CATALOG META SECTION */}
      <AiMetaSection data={data} isMobile={isMobile} />

      {/* SEARCH RESULTS SECTION */}
      <SearchResultsSection data={data} isMobile={isMobile} />

      {/* PRODUCT DATA SECTION */}
      <ProductDataSection data={data} isMobile={isMobile} />

      {/* CLASSIFICATION SECTION */}
      <ClassificationSection data={data} isMobile={isMobile} />

      {/* TECHNICAL CHARACTERISTICS SECTION */}
      <TechnicalSection data={data} isMobile={isMobile} />

      {/* FISCAL DATA SECTION (physical/kit only) */}
      {isPhysicalOrKit && <FiscalDataSection data={data} isMobile={isMobile} />}

      {/* MANUFACTURER SECTION */}
      <ManufacturerSection data={data} isMobile={isMobile} />

      {/* PRICING SECTION */}
      <PricingSection data={data} isMobile={isMobile} />

      {/* DIMENSIONS SECTION (physical/kit only) */}
      {isPhysicalOrKit && <DimensionsSection data={data} isMobile={isMobile} />}

      {/* SEGMENT-SPECIFIC SECTIONS */}
      {segment === 'insurance' && <InsuranceDataSection data={data} isMobile={isMobile} />}
      {segment === 'software_license' && <LicenseDataSection data={data} isMobile={isMobile} />}
      {segment === 'saas' && <SubscriptionDataSection data={data} isMobile={isMobile} />}
      {segment === 'infoproduct' && <InfoproductDataSection data={data} isMobile={isMobile} />}
      {segment === 'realestate' && <RealestateDataSection data={data} isMobile={isMobile} />}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AiInfoTab;
